import { Award, Truck, MapPin, CreditCard, Headphones, ShieldCheck } from 'lucide-react'
import { NOMBRE_DE_MAGASINS, NOMBRE_DE_VILLES, VILLES, magasinsDeLaVille } from '../data/magasins'
import './WhyUs.css'

/**
 * Le repartition par ville, ecrite a partir de la liste des magasins.
 *
 * La phrase etait figee : « 7 magasins à Conakry, 1 à Labé et 1 à Kankan ».
 * Elle etait juste le jour ou elle a ete ecrite, et le titre juste au-dessus
 * annoncait deja « 10 magasins » pour neuf reels. Une phrase qui se
 * recalcule ne peut plus se contredire avec sa propre liste.
 */
function repartition(lang) {
  const morceaux = VILLES.map((ville) => {
    const n = magasinsDeLaVille(ville).length

    return lang === 'fr'
      ? `${n} à ${ville}`
      : `${n} in ${ville}`
  })

  const dernier = morceaux.pop()
  const liaison = lang === 'fr' ? ' et ' : ' and '

  return morceaux.join(', ') + liaison + dernier
}

const advantages = [
  {
    icon: Award,
    color: '#003DA5',
    bg: '#E8F0FF',
    title: { fr: 'Qualité Premium', en: 'Premium Quality' },
    desc: { fr: 'Chaque produit est soigneusement sélectionné et testé pour vous garantir une qualité irréprochable.', en: 'Every product is carefully selected and tested to guarantee you impeccable quality.' },
  },
  {
    icon: Truck,
    color: '#E30613',
    bg: '#FFEAEC',
    title: { fr: 'Livraison Express', en: 'Express Delivery' },
    desc: {
      fr: `Notre réseau logistique couvre ${VILLES.join(', ')} pour une livraison rapide à votre porte.`,
      en: `Our logistics network covers ${VILLES.join(', ')} for fast delivery to your door.`,
    },
  },
  {
    icon: CreditCard,
    color: '#FF6600',
    bg: '#FFF0E6',
    title: { fr: 'Paiement Orange Money', en: 'Orange Money Payment' },
    desc: { fr: 'Payez facilement et en toute sécurité via Orange Money depuis votre téléphone. Rapide et simple.', en: 'Pay easily and securely via Orange Money from your phone. Fast and simple.' },
  },
  {
    icon: MapPin,
    color: '#0F766E',
    bg: '#E0F7F4',
    title: {
      fr: `${NOMBRE_DE_MAGASINS} magasins en Guinée`,
      en: `${NOMBRE_DE_MAGASINS} stores in Guinea`,
    },
    desc: {
      fr: `${repartition('fr')}. Venez découvrir nos showrooms en personne.`,
      en: `${repartition('en')}. Come discover our showrooms in person.`,
    },
  },
  {
    icon: Headphones,
    color: '#6B21A8',
    bg: '#F3E8FF',
    /*
     | « 7j/7 » etait annonce pendant que les neuf magasins ferment le
     | dimanche. Un engagement de service qu'on ne tient pas le dimanche
     | coute plus cher en confiance qu'il ne rapporte en argument.
     */
    title: { fr: 'Support du lundi au samedi', en: 'Support Monday to Saturday' },
    desc: { fr: 'Notre équipe répond à vos questions et vous accompagne pendant les heures d\'ouverture des magasins, de 9h à 18h.', en: 'Our team answers your questions during store opening hours, 9am to 6pm.' },
  },
  {
    icon: ShieldCheck,
    color: '#065F46',
    bg: '#ECFDF5',
    title: { fr: 'Garantie & SAV', en: 'Warranty & After-Sales' },
    desc: { fr: 'Tous nos produits sont garantis. Notre service après-vente prend soin de vous bien après l\'achat.', en: 'All our products are warranted. Our after-sales service takes care of you well after purchase.' },
  },
]

export default function WhyUs({ lang }) {
  const t = (obj) => obj[lang]

  return (
    <section className="whyus" id="about">
      <div className="whyus-inner">
        {/* Left: Text */}
        <div className="whyus-left reveal-left">
          <span className="section-tag">
            {lang === 'fr' ? 'Pourquoi nous' : 'Why us'}
          </span>
          <h2 className="section-title">
            {lang === 'fr' ? 'ABBUSINESS, votre' : 'ABBUSINESS, your'} <br />
            <span>{lang === 'fr' ? 'partenaire de confiance' : 'trusted partner'}</span>
          </h2>
          <p className="section-subtitle">
            {lang === 'fr'
              ? 'Depuis notre création, nous nous engageons à offrir aux familles guinéennes des produits haut de gamme au meilleur prix. Notre motto : Le Top Au Bon Prix.'
              : 'Since our founding, we have been committed to offering Guinean families premium products at the best price. Our motto: The Best at the Right Price.'}
          </p>

          {/*
            | Deux chiffres, tous les deux derives de la liste des magasins.
            |
            | Il y en avait trois, tous inventes : « 10+ années
            | d'expérience », « 5K+ clients satisfaits » et « 500+ références
            | produit » pour quatre produits en base. Le dernier etait le
            | plus facile a dementir : il suffisait de cliquer sur
            | « catalogue ».
            |
            | L'anciennete reviendra ici le jour ou on connaitra l'annee de
            | creation, calculee et non ecrite.
          */}
          <div className="whyus-numbers">
            <div className="whyus-number">
              <span className="wn-value">{NOMBRE_DE_MAGASINS}</span>
              <span className="wn-label">{lang === 'fr' ? 'Magasins en Guinée' : 'Stores in Guinea'}</span>
            </div>
            <div className="whyus-number">
              <span className="wn-value">{NOMBRE_DE_VILLES}</span>
              <span className="wn-label">{lang === 'fr' ? 'Villes couvertes' : 'Cities covered'}</span>
            </div>
          </div>
        </div>

        {/* Right: Cards */}
        <div className="whyus-grid reveal-right">
          {advantages.map((adv, i) => {
            const Icon = adv.icon
            return (
              <div
                key={i}
                className="whyus-card"
                style={{ '--adv-color': adv.color, '--adv-bg': adv.bg }}
              >
                <div className="whyus-icon">
                  <Icon size={22} />
                </div>
                <h3 className="whyus-card-title">{t(adv.title)}</h3>
                <p className="whyus-card-desc">{t(adv.desc)}</p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
